/**
 * Speech segmentation + read-duration estimation for auto-host voice mode (#91).
 *
 * Pure transforms over the {@link SpeechSegment} contract, so they live in `shared`
 * (AGENT_GUIDE §5): the **server** uses {@link estimateSpeechMs} to schedule the fair
 * buzzer-open moment (D1), and the **client** uses {@link deriveSpeechSegments} to
 * synth pauses for a clue that carries none (uploaded packs, D7). One definition, no
 * contract drift between the two sites.
 */

import type { SpeechSegment } from './domain';

/**
 * Default dramatic pause (ms) inserted after a clause when segmenting heuristically.
 * A mid-clause comma gets a shorter beat than a sentence end.
 */
const CLAUSE_PAUSE_MS = 180;
const SENTENCE_PAUSE_MS = 320;

/**
 * Words-per-minute the TTS voice is assumed to read at (at ~0.9 rate — the
 * game-show delivery in the client wrapper). Used to estimate spoken duration.
 */
export const DEFAULT_WORDS_PER_MINUTE = 150;

/** Count whitespace-delimited words in a chunk of text. */
function wordCount(text: string): number {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * Derive read-aloud segments from a plain prompt by splitting on clause/sentence
 * punctuation (the client fallback when a clue has no authored `speech`, D7).
 * Concatenating the segment `text` reconstructs the prompt (modulo the whitespace
 * that surrounded the split points), so no words are dropped. Never returns an
 * empty array for non-empty input; the final segment always has `pauseMsAfter: 0`.
 */
export function deriveSpeechSegments(prompt: string): SpeechSegment[] {
  const text = prompt.trim();
  if (text === '') return [];

  // Split AFTER clause/sentence punctuation, keeping the delimiter with its chunk.
  const rawChunks = text.match(/[^,.;:!?]+[,.;:!?]*\s*/g) ?? [text];
  const chunks = rawChunks.map((c) => c.trim()).filter((c) => c.length > 0);
  if (chunks.length === 0) return [{ text, pauseMsAfter: 0 }];

  return chunks.map((chunk, i) => {
    const isLast = i === chunks.length - 1;
    if (isLast) return { text: chunk, pauseMsAfter: 0 };
    const endsSentence = /[.!?]$/.test(chunk);
    return {
      text: chunk,
      pauseMsAfter: endsSentence ? SENTENCE_PAUSE_MS : CLAUSE_PAUSE_MS,
    };
  });
}

/**
 * Resolve the segments to read for a clue: the authored `speech` when present and
 * non-empty (generator-emitted, D7), otherwise the heuristic derivation from the
 * prompt. Always returns at least one segment for a non-empty prompt.
 */
export function resolveSpeechSegments(
  speech: SpeechSegment[] | undefined,
  prompt: string,
): SpeechSegment[] {
  if (speech && speech.length > 0) return speech;
  return deriveSpeechSegments(prompt);
}

/**
 * Estimate the wall-clock (ms) to read the segments aloud: spoken word time at
 * {@link DEFAULT_WORDS_PER_MINUTE} plus the sum of the encoded pauses. The server
 * uses this to arm the fair buzzer-open timer (D1). Deterministic and clock-free.
 */
export function estimateSpeechMs(
  segments: SpeechSegment[],
  wordsPerMinute: number = DEFAULT_WORDS_PER_MINUTE,
): number {
  const wpm = wordsPerMinute > 0 ? wordsPerMinute : DEFAULT_WORDS_PER_MINUTE;
  const words = segments.reduce((n, s) => n + wordCount(s.text), 0);
  const speakMs = (words / wpm) * 60_000;
  const pauseMs = segments.reduce((n, s) => n + Math.max(0, s.pauseMsAfter), 0);
  return Math.round(speakMs + pauseMs);
}
